import * as React from "react";
import { Grid, Dialog, CircularProgress } from "@mui/material";
import {
  TextInput,
  useRecordContext,
  required,
  useGetIdentity,
} from "react-admin";
import { DialogueContext } from "../../../Components/ActionsMenu";
import { CreateForm } from "../../../Layout/CreateForm";
import { getConversionRate } from "../../../Provider/parseDataProvider";
import calculateFinalAmount from "../../../Utilities/mintPercentage";
import { Parse } from "parse";

Parse.initialize(
  process.env.REACT_APP_APP_ID,
  process.env.REACT_APP_JAVASCRIPT_KEY,
  process.env.REACT_APP_MASTER_KEY
);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

export const RechargeForm = ({ handleClose, record }) => {
  const { data } = useGetIdentity();

  const [loading, setLoading] = React.useState(false);
  const [conversionRate, setConversionRate] = React.useState(null);
  const [inputValue, setInputValue] = React.useState(""); // State for the input value
  const [managerBalance, setManagerBalance] = React.useState();
  const [calculatedValue, setCalculatedValue] = React.useState(null);

  React.useEffect(() => {
    const fetchManagerBalance = async () => {
      const query = new Parse.Query(Parse.User);
      query
        .get(record?.manager?.id)
        .then((user) => {
          const managerBalance = user.get("balance");
          setManagerBalance(managerBalance);
        })
        .catch((error) => {
          console.error("Error fetching manager balance:", error);
        });
    };

    fetchManagerBalance();
  }, []);

  React.useEffect(() => {
    const fetchConversionRate = async () => {
      setLoading(true);
      try {
        const rate = await getConversionRate(record?.role); // Pass record?.role
        setConversionRate(rate);
      } finally {
        setLoading(false);
      }
    };

    if (record?.role) {
      // Only fetch if record.role exists
      fetchConversionRate();
    }
  }, [record?.role]); // Add record?.role to dependency array

  const handleInputChange = async (event) => {
    const newValue = event.target.value;
    setInputValue(newValue);

    if (conversionRate && newValue) {
      // Calculate only if rate and input are available
      const result = await calculateFinalAmount(
        Number(newValue),
        Number(conversionRate)
      );
      setCalculatedValue(result);
    } else {
      setCalculatedValue(null); // Reset if either rate or input is missing
    }
  };

  const validateAmount = (value) => {
    if (!value) return "Required";
    if (!/^\d+(\.\d{1,2})?$/.test(value)) {
      return "Amount must be a positive number with up to 2 decimal places";
    }
    if (parseInt(value, 10) <= 0) return "Amount must be greater than zero";
    if (
      data?.userRoleName !== "Super-User" &&
      parseInt(value, 10) > managerBalance
    ) {
      return `Amount cannot exceed (${managerBalance?.toFixed(2)})`;
    }
    return undefined;
  };

  return (
    <CreateForm
      title="Recharge User"
      handleClose={handleClose}
      record={record}
      resource="rechargeRecords"
    >
      {loading ? (
        <CircularProgress />
      ) : (
        <>
          <Grid item xs={12}>
            <TextInput
              label="Account"
              id="username"
              source="username"
              readOnly
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              id="txn_type"
              source="txn_type"
              name="txn_type"
              label="Recharge"
              defaultValue="Recharge"
              readOnly
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextInput
              id="amount"
              name="amount"
              label="Recharge Amount"
              fullWidth
              validate={[required(), validateAmount]}
              onInput={(e) => {
                const value = e.target.value;

                // Allow only numbers and a single dot
                let sanitized = value.replace(/[^0-9.]/g, "");

                // Prevent multiple dots
                const parts = sanitized.split(".");
                if (parts.length > 2) {
                  sanitized = parts[0] + "." + parts[1];
                }

                // Restrict to 2 decimal places
                if (parts[1]?.length > 2) {
                  sanitized = parts[0] + "." + parts[1].slice(0, 2);
                }

                e.target.value = sanitized;
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              onChange={handleInputChange}
            />
          </Grid>

          {conversionRate !== null && !loading && (
            // Display rate if fetched and not loading
            <div
              style={{
                backgroundColor: "rgba(30, 30, 30, 0.5)",
                padding: "5px",
                borderRadius: "3px",
                marginBottom: "5px",
              }}
            >
              {data?.userRoleName !== "Super-User" && managerBalance !== undefined && managerBalance !== null ? (
                <>
                  <span
                    style={{
                      color: "#FFD700",
                      fontWeight: "bold",
                      fontSize: "0.75em",
                    }}
                  >
                    Maximum Amount Recharge: {managerBalance?.toFixed(2)}
                  </span>
                  <br />
                </>
              ) : null}

              <span
                style={{
                  color: "#FFD700",
                  fontWeight: "bold",
                  fontSize: "0.75em",
                }}
              >
                Coins to be Credited:
              </span>
              <span
                style={{
                  color: "#FFD700",
                  fontWeight: "bold",
                  fontSize: "0.75em",
                }}
              >
                &nbsp; &nbsp; {calculatedValue !== null ? calculatedValue : 0}
              </span>
            </div>
          )}
          <Grid item xs={12}>
            <TextInput id="remarks" name="remarks" label="Remark" fullWidth   onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}/>
          </Grid>
        </>
      )}
    </CreateForm>
  );
};

export const RechargeBalanceFormDialog = ({
  openDialog,
  handleDialogClose,
}) => {
  const { handleMenuClose } = React.useContext(DialogueContext);
  const handleClose = () => {
    handleDialogClose();
    handleMenuClose();
  };
  const record = useRecordContext();

  return (
    <Dialog
      open={openDialog}
      onClose={handleClose}
      onKeyDown={(evt) => evt.stopPropagation()}
    >
      <RechargeForm handleClose={handleClose} record={record} />
    </Dialog>
  );
};
